'use client';

import React, { useState, useEffect, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface OrderData {
  id: string;
  full_name: string;
  company_name: string;
  nip: string;
  email: string | null;
  phone: string | null;
  preferred_login: string | null;
  invoice_volume: 'under_3' | 'over_3';
  payment_method: 'online' | 'transfer';
  status: 'new' | 'awaiting_payment' | 'paid' | 'in_progress' | 'done';
  created_at: string;
  updated_at: string;
}

interface FormData {
  orderId: string;
  email: string;
}

const initialForm: FormData = {
  orderId: '',
  email: '',
};

function TrackOrderContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [form, setForm] = useState<FormData>(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [updateNotification, setUpdateNotification] = useState(false);
  const [realtimeError, setRealtimeError] = useState<string | null>(null);

  // Pre-fill order ID from URL parameter
  useEffect(() => {
    const orderIdParam = searchParams.get('order_id');
    if (orderIdParam) {
      setForm((prev) => ({ ...prev, orderId: orderIdParam }));
    }
  }, [searchParams]);

  // Real-time subscription effect
  useEffect(() => {
    if (!orderData?.id) return;

    const supabase = createClient();
    
    // Create a channel for real-time updates
    const channel = supabase
      .channel(`order-${orderData.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders_ksef',
          filter: `id=eq.${orderData.id}`,
        },
        (payload) => {
          console.log('Real-time update received:', payload);
          
          // Update order data with new values
          if (payload.new) {
            setOrderData(payload.new as OrderData);
            
            // Show update notification
            setUpdateNotification(true);
            
            // Hide notification after 3 seconds
            setTimeout(() => {
              setUpdateNotification(false);
            }, 3000);
          }
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
        
        if (status === 'SUBSCRIPTION_ERROR') {
          setRealtimeError('Błąd połączenia z aktualizacjami na żywo');
        } else if (status === 'SUBSCRIBED') {
          setRealtimeError(null);
          console.log('Successfully subscribed to real-time updates');
        }
      });

    // Cleanup subscription on unmount or when order changes
    return () => {
      console.log('Cleaning up real-time subscription');
      supabase.removeChannel(channel);
    };
  }, [orderData?.id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setOrderData(null);

    // Validation
    if (!form.orderId || !form.email) {
      setError('Proszę wypełnić wszystkie pola.');
      setLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setError('Proszę podać prawidłowy adres e-mail.');
      setLoading(false);
      return;
    }

    try {
      const supabase = createClient();
      
      // Query order by ID and email
      const { data, error: queryError } = await supabase
        .from('orders_ksef')
        .select('*')
        .eq('id', form.orderId)
        .eq('email', form.email)
        .single();

      if (queryError) {
        if (queryError.code === 'PGRST116') {
          setError('Nie znaleziono zamówienia o podanym numerze i adresie e-mail. Sprawdź poprawność danych.');
        } else {
          console.error('Query error:', queryError);
          setError('Wystąpił błąd podczas wyszukiwania zamówienia. Spróbuj ponownie.');
        }
        setLoading(false);
        return;
      }

      if (!data) {
        setError('Nie znaleziono zamówienia. Sprawdź numer zamówienia i adres e-mail.');
        setLoading(false);
        return;
      }

      setOrderData(data as OrderData);
    } catch (err: any) {
      console.error('Error:', err);
      setError('Wystąpił błąd podczas wyszukiwania zamówienia. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status: OrderData['status']) => {
    const configs = {
      new: {
        label: 'Nowe',
        color: 'bg-gray-100 text-gray-700 border-gray-300',
        icon: 'DocumentTextIcon',
        iconColor: 'text-gray-600',
      },
      awaiting_payment: {
        label: 'Oczekuje na płatność',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        icon: 'ClockIcon',
        iconColor: 'text-yellow-600',
      },
      paid: {
        label: 'Opłacone',
        color: 'bg-green-100 text-green-800 border-green-300',
        icon: 'CheckCircleIcon',
        iconColor: 'text-green-600',
      },
      in_progress: {
        label: 'W realizacji',
        color: 'bg-blue-100 text-blue-800 border-blue-300',
        icon: 'CogIcon',
        iconColor: 'text-blue-600',
      },
      done: {
        label: 'Zrealizowane',
        color: 'bg-green-100 text-green-800 border-green-300',
        icon: 'CheckBadgeIcon',
        iconColor: 'text-green-600',
      },
    };
    return configs[status];
  };

  const getProgressSteps = (currentStatus: OrderData['status']) => {
    const steps = [
      { key: 'new', label: 'Zamówienie otrzymane', statuses: ['new'] },
      { key: 'payment', label: 'Płatność', statuses: ['awaiting_payment', 'paid'] },
      { key: 'in_progress', label: 'W realizacji', statuses: ['in_progress'] },
      { key: 'done', label: 'Wdrożenie zakończone', statuses: ['done'] },
    ];

    const statusOrder = ['new', 'awaiting_payment', 'paid', 'in_progress', 'done'];
    const currentIndex = statusOrder.indexOf(currentStatus);

    return steps.map((step, index) => {
      const stepStatuses = step.statuses;
      const stepMaxIndex = Math.max(...stepStatuses.map(s => statusOrder.indexOf(s)));
      const isCompleted = currentIndex > stepMaxIndex;
      const isCurrent = stepStatuses.includes(currentStatus);
      
      return {
        ...step,
        isCompleted,
        isCurrent,
        isPending: !isCompleted && !isCurrent,
      };
    });
  };

  const getNextSteps = (status: OrderData['status'], paymentMethod: OrderData['payment_method']) => {
    const steps: { [key: string]: { title: string; description: string; timeline: string } } = {
      new: {
        title: 'Oczekiwanie na potwierdzenie',
        description: 'Twoje zamówienie zostało przyjęte. Wkrótce otrzymasz instrukcje dotyczące płatności.',
        timeline: 'W ciągu 24 godzin',
      },
      awaiting_payment: {
        title: paymentMethod === 'online' ? 'Oczekiwanie na płatność online' : 'Oczekiwanie na przelew',
        description: paymentMethod === 'online' ?'Prosimy o dokonanie płatności online, aby kontynuować realizację zamówienia.' :'Prosimy o dokonanie przelewu zgodnie z instrukcjami wysłanymi na e-mail.',
        timeline: paymentMethod === 'online' ? 'Natychmiast' : '1-3 dni robocze',
      },
      paid: {
        title: 'Płatność potwierdzona',
        description: 'Twoja płatność została zaksięgowana. Nasz zespół wkrótce rozpocznie wdrożenie KSeF.',
        timeline: '1-2 dni robocze',
      },
      in_progress: {
        title: 'Wdrożenie w toku',
        description: 'Nasz zespół pracuje nad konfiguracją Twojego systemu KSeF. Skontaktujemy się z Tobą w sprawie szczegółów.',
        timeline: '3-5 dni roboczych',
      },
      done: {
        title: 'Wdrożenie zakończone',
        description: 'Twój system KSeF jest gotowy do użycia. Dziękujemy za zaufanie!',
        timeline: 'Zakończone',
      },
    };
    return steps[status];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInvoiceVolumeText = (volume: OrderData['invoice_volume']) => {
    return volume === 'under_3' ?'Do 3 faktur miesięcznie (darmowy program)' :'Powyżej 3 faktur miesięcznie (od 9,90 zł/mies.)';
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-navy pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {/* Real-time update notification */}
          {updateNotification && (
            <div className="fixed top-24 right-4 z-50 animate-slide-in-right">
              <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                <span className="font-600 text-sm">Status zamówienia zaktualizowany</span>
                <Icon name="CheckCircleIcon" className="w-5 h-5" />
              </div>
            </div>
          )}

          {/* Real-time connection error */}
          {realtimeError && orderData && (
            <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-2">
              <Icon name="ExclamationTriangleIcon" className="w-5 h-5 text-yellow-600 flex-shrink-0" />
              <p className="text-sm text-yellow-800">{realtimeError}</p>
            </div>
          )}

          {/* Back to home link */}
          <button
            onClick={() => router.push('/landing')}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-6 group"
          >
            <Icon name="ArrowLeftIcon" className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-600">Powrót do strony głównej</span>
          </button>

          {!orderData ? (
            /* Search Form */
            <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 md:p-12">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <Icon name="MagnifyingGlassIcon" className="w-10 h-10 text-blue-600" />
                </div>
              </div>

              <h1
                className="text-2xl sm:text-3xl md:text-4xl font-700 text-navy text-center mb-3"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                Śledź status zamówienia
              </h1>
              <p className="text-base sm:text-lg text-gray-600 text-center mb-8">
                Wprowadź numer zamówienia i adres e-mail, aby sprawdzić status realizacji
              </p>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                  <Icon name="ExclamationTriangleIcon" className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-red-800 font-600">{error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="orderId" className="block text-sm font-700 text-navy mb-2">
                    Numer zamówienia <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="orderId"
                    name="orderId"
                    value={form.orderId}
                    onChange={handleChange}
                    placeholder="np. 123e4567-e89b-12d3-a456-426614174000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-navy font-mono text-sm"
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Numer zamówienia znajdziesz w potwierdzeniu wysłanym na e-mail
                  </p>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-700 text-navy mb-2">
                    Adres e-mail <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="twoj@email.pl"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-navy"
                    disabled={loading}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Adres e-mail podany podczas składania zamówienia
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue text-white font-700 py-4 px-6 rounded-lg hover:bg-blue-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Icon name="ArrowPathIcon" className="w-5 h-5 animate-spin" />
                      <span>Wyszukiwanie...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="MagnifyingGlassIcon" className="w-5 h-5" />
                      <span>Sprawdź status</span>
                    </>
                  )}
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center">
                  Nie możesz znaleźć swojego zamówienia? Skontaktuj się z nami:
                  <br />
                  <a href="mailto:kontakt.emtgroup@gmail.com" className="text-blue hover:underline font-600">
                    kontakt.emtgroup@gmail.com
                  </a>
                  {' '} lub {' '}
                  <a href="tel:+48882639186" className="text-blue hover:underline font-600">
                    882-639-186
                  </a>
                </p>
              </div>
            </div>
          ) : (
            /* Order Details Display */
            <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 md:p-12">
              {/* Status Badge */}
              <div className="flex justify-center mb-6">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 ${getStatusConfig(orderData.status).color}`}>
                  <Icon name={getStatusConfig(orderData.status).icon} className={`w-6 h-6 ${getStatusConfig(orderData.status).iconColor}`} />
                  <span className="font-700 text-sm uppercase tracking-wide">
                    {getStatusConfig(orderData.status).label}
                  </span>
                </div>
              </div>

              <h1
                className="text-2xl sm:text-3xl md:text-4xl font-700 text-navy text-center mb-3"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                Status zamówienia
              </h1>

              {/* Order Reference */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 text-center">
                <p className="text-sm text-gray-600 mb-1">Numer zamówienia</p>
                <p className="text-lg sm:text-xl font-700 text-navy font-mono break-all">{orderData.id}</p>
                <p className="text-xs text-gray-500 mt-2">Złożone: {formatDate(orderData.created_at)}</p>
              </div>

              {/* Progress Timeline */}
              <div className="mb-8">
                <h2
                  className="text-xl font-700 text-navy border-b border-gray-200 pb-2 mb-6"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  Postęp realizacji
                </h2>
                <div className="relative">
                  {/* Progress line */}
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />
                  
                  <div className="space-y-6">
                    {getProgressSteps(orderData.status).map((step, index) => (
                      <div key={step.key} className="relative flex items-start gap-4">
                        {/* Step circle */}
                        <div
                          className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center font-700 text-sm ${
                            step.isCompleted
                              ? 'bg-green-500 text-white'
                              : step.isCurrent
                              ? 'bg-blue text-white' :'bg-gray-200 text-gray-500'
                          }`}
                        >
                          {step.isCompleted ? (
                            <Icon name="CheckIcon" className="w-6 h-6" />
                          ) : (
                            <span>{index + 1}</span>
                          )}
                        </div>
                        {/* Step content */}
                        <div className="flex-1 pt-2">
                          <p
                            className={`font-700 text-base ${
                              step.isCompleted || step.isCurrent ? 'text-navy' : 'text-gray-400'
                            }`}
                          >
                            {step.label}
                          </p>
                          {step.isCurrent && (
                            <p className="text-sm text-gray-600 mt-1">
                              {step.key === 'payment' && orderData.status === 'awaiting_payment' ?'Oczekiwanie na płatność'
                                : step.key === 'payment'&& orderData.status === 'paid' ?'Płatność potwierdzona' :'W trakcie'}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Next Steps */}
              <div className="mb-8">
                <h2
                  className="text-xl font-700 text-navy border-b border-gray-200 pb-2 mb-4"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  Następne kroki
                </h2>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <div className="flex items-start gap-3">
                    <Icon name="InformationCircleIcon" className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h3 className="text-lg font-700 text-navy mb-2">
                        {getNextSteps(orderData.status, orderData.payment_method).title}
                      </h3>
                      <p className="text-sm text-gray-700 mb-3">
                        {getNextSteps(orderData.status, orderData.payment_method).description}
                      </p>
                      <div className="flex items-center gap-2 text-sm">
                        <Icon name="ClockIcon" className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-600">
                          Szacowany czas: <strong>{getNextSteps(orderData.status, orderData.payment_method).timeline}</strong>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Details */}
              <div className="mb-8">
                <h2
                  className="text-xl font-700 text-navy border-b border-gray-200 pb-2 mb-4"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  Szczegóły zamówienia
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">Imię i nazwisko</p>
                    <p className="text-base font-600 text-navy">{orderData.full_name}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">Nazwa firmy</p>
                    <p className="text-base font-600 text-navy">{orderData.company_name}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">NIP</p>
                    <p className="text-base font-600 text-navy font-mono">{orderData.nip}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">Kontakt</p>
                    <p className="text-base font-600 text-navy break-all">
                      {orderData.email || orderData.phone}
                    </p>
                    {orderData.preferred_login && (
                      <p className="text-xs text-gray-600 mt-1">Login: {orderData.preferred_login}</p>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">Pakiet</p>
                    <p className="text-base font-600 text-navy">{getInvoiceVolumeText(orderData.invoice_volume)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">Metoda płatności</p>
                    <p className="text-base font-600 text-navy">
                      {orderData.payment_method === 'online' ? 'Płatność online' : 'Przelew bankowy'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => {
                    setOrderData(null);
                    setForm(initialForm);
                    setError(null);
                    setRealtimeError(null);
                  }}
                  className="flex-1 bg-gray-100 text-navy font-700 py-3 px-6 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                >
                  <Icon name="MagnifyingGlassIcon" className="w-5 h-5" />
                  <span>Sprawdź inne zamówienie</span>
                </button>
                <a
                  href="mailto:kontakt.emtgroup@gmail.com"
                  className="flex-1 bg-blue text-white font-700 py-3 px-6 rounded-lg hover:bg-blue-light transition-colors flex items-center justify-center gap-2"
                >
                  <Icon name="EnvelopeIcon" className="w-5 h-5" />
                  <span>Skontaktuj się z nami</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

export default function TrackOrderPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <TrackOrderContent />
    </Suspense>
  );
}